export type TpFacebookValueParameters = {
  value?: number | string;
  currency?: string;
};

const normalizeParameterValue = (
  value: null | number | string | undefined,
): string | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const normalizeCurrency = (currency: string | undefined) => {
  const trimmedCurrency = currency?.trim();
  return trimmedCurrency && trimmedCurrency.length > 0
    ? trimmedCurrency
    : undefined;
};

export const getSelectedFacebookPredictedLtv = ({
  isUsFeatureEnabled,
  pltv,
  usPltv,
}: {
  isUsFeatureEnabled: boolean;
  pltv?: null | string;
  usPltv?: null | string;
}) => normalizeParameterValue(isUsFeatureEnabled ? usPltv : pltv);

export const getFacebookPurchaseParameters = ({
  analyticsParameters,
  predictedLtv,
}: {
  analyticsParameters: TpFacebookValueParameters;
  predictedLtv?: string;
}): TpFacebookValueParameters => {
  if (!predictedLtv) {
    return analyticsParameters;
  }

  return {
    ...analyticsParameters,
    currency: 'USD',
    value: predictedLtv,
  };
};

export const getFacebookValueCustomData = ({
  currency,
  value,
}: TpFacebookValueParameters) => {
  const normalizedCurrency = normalizeCurrency(currency);
  const normalizedValue = normalizeParameterValue(value);

  return {
    ...(normalizedCurrency && { currency: normalizedCurrency }),
    ...(normalizedValue && { value: normalizedValue }),
  };
};

export const getFacebookPredictedLtvCustomData = (predictedLtv?: string) => {
  const normalizedPredictedLtv = normalizeParameterValue(predictedLtv);

  if (!normalizedPredictedLtv) {
    return {};
  }

  const numericPredictedLtv = Number(normalizedPredictedLtv);

  return {
    predicted_ltv: Number.isFinite(numericPredictedLtv)
      ? numericPredictedLtv
      : normalizedPredictedLtv,
  };
};

export const getFacebookPurchaseCustomData = ({
  predictedLtv,
  purchaseParameters,
}: {
  predictedLtv?: string;
  purchaseParameters: TpFacebookValueParameters;
}) => ({
  ...getFacebookValueCustomData(purchaseParameters),
  ...getFacebookPredictedLtvCustomData(predictedLtv),
});

export const getFacebookSubscribeCustomData = ({
  analyticsParameters,
  predictedLtv,
}: {
  analyticsParameters: TpFacebookValueParameters;
  predictedLtv?: string;
}) => ({
  ...getFacebookValueCustomData(analyticsParameters),
  ...getFacebookPredictedLtvCustomData(predictedLtv),
});
